import { useSWRMutation } from '@tiger/swr';
import { Button, Checkbox, Form } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { $Formutil, withForm } from 'react-antd-formutil';
import { useLocation, useNavigate } from 'react-router-dom';
import BankCardSelect, { BankCardAuthStatus, BankCardItemTypes } from 'components/BankCardSelect';
import { CardButtonWrap } from 'components/Card';
import Debug from 'components/Debug';
import ErrorBox from 'components/ErrorBox';
import UploaderList from 'components/FileUploader/UploaderList';
import FormItem from 'components/FormItem';
import HashLink from 'components/HashLink';
import Loading from 'components/Loading';
import Toast from 'components/Toast';
import { useGetBankCards } from 'modules/BankCards/request';
import security from 'stores/security';
import { Source } from 'types/funding';
import API from 'utils/API';
import http from 'utils/http';
import { language, printf } from 'utils/i18n';
import { getMaskText } from 'utils/util';
import { BankCardErrorCodeTypes, customErrorMsgMap } from './config';
import './style.scss';

interface ManualProps {
    $formutil: $Formutil;
}

const Manual: React.FC<ManualProps> = ({ $formutil }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as { id: BankCardItemTypes['id']; source: Source } | null;
    const { $invalid, $batchState, $params, $getFirstError, $setValues } = $formutil;
    const {
        isMutating,
        error,
        data,
        trigger: getBankCardTrigger
    } = useGetBankCards({
        queryType: 'manage',
        // ach手动认证只展示美国银行卡
        country: 'US'
    });
    const { trigger } = useSWRMutation(
        'bankcards.auth.manual.submit',
        ({ id, formData }: { id: BankCardItemTypes['id']; formData: FormData }) =>
            http.post(API.TRADE.bankcards.uploadAuth(id), formData)
    );
    const [currentBankInfo, setCurrentBankInfo] = useState<BankCardItemTypes>();
    const { holderName, bankAccount } = currentBankInfo || {};

    useEffect(() => {
        getBankCardTrigger();
    }, [getBankCardTrigger]);

    const bankcardList = useMemo(() => {
        // 审核通过的银行卡不展示
        return data?.filter(
            ({ status }) => ![BankCardAuthStatus.Audited, BankCardAuthStatus.Unaudited].includes(status)
        );
    }, [data]);

    useEffect(() => {
        if (bankcardList?.length) {
            const curr = bankcardList?.find(item => item.id === locationState?.id) || bankcardList?.[0];

            setCurrentBankInfo(curr);

            $setValues({
                bankAccount: curr.bankAccount
            });
        }
    }, [bankcardList, locationState, $setValues]);

    const clearLocationStateId = () => {
        navigate('', {
            replace: true,
            state: {
                ...locationState,
                id: undefined
            }
        });
    };

    const handleAdd = () => {
        clearLocationStateId();

        navigate('/profile/bankcards/add', {
            state: {
                source: 'authentication'
            }
        });
    };

    const handleEdit = (bankcardInfo: BankCardItemTypes) => {
        /**
         * 如果缓存的id和去编辑的id不一致则进入编辑页时
         * 清除当前页面id的缓存，以编辑成功后最新更新的一项为准
         */
        if (locationState?.id !== bankcardInfo.id) {
            clearLocationStateId();
        }

        navigate(`/profile/bankcards/edit/${bankcardInfo.id}`, {
            state: {
                source: 'authentication'
            }
        });
    };

    const handleChange = (bankAccount: BankCardItemTypes['bankAccount']) => {
        setCurrentBankInfo(bankcardList!.find(item => item.bankAccount === bankAccount));
    };

    const handleSubmit = async () => {
        if ($invalid) {
            $batchState({
                $touched: true,
                $dirty: true
            });

            const errorText = $getFirstError();

            Toast.show(customErrorMsgMap[errorText] || errorText);
        } else {
            Toast.loading(true);

            try {
                const formData = new FormData();

                if ($params.files?.[0]?.file) {
                    $params.files.forEach(({ file }) => {
                        if (file) {
                            formData.append('files', file, file.name);
                        }
                    });
                }

                await trigger({ id: currentBankInfo!.id, formData });

                Toast.loading(false);
                await Toast.show(__('上传成功'));

                if (locationState?.source === 'withdraw') {
                    navigate('../complete', {
                        state: {
                            next: '/profile/withdraw/ach'
                        }
                    });
                } else {
                    navigate(-1);
                }
            } catch (err) {
                Toast.loading(false);
                Toast.show(err);
            }
        }
    };

    const render = () => {
        if (isMutating) {
            return <Loading />;
        }

        if (error) {
            return <ErrorBox error={error} onClick={() => getBankCardTrigger()} />;
        }

        return (
            <Form layout="vertical" onFinish={handleSubmit}>
                <div className="space-box">
                    <BankCardSelect
                        data={bankcardList}
                        filterFiledValue="bankAccount"
                        formItemConfig={{
                            name: 'bankAccount',
                            label: __('选择/添加银行账户'),
                            checker: (bankAccount: BankCardItemTypes['bankAccount']) => {
                                const currentBankInfo = bankcardList?.find(item => item.bankAccount === bankAccount);

                                if (currentBankInfo) {
                                    if (!bankAccount) {
                                        return __('请添加银行卡');
                                    }

                                    const { clearingCode, bankAccountMainType } = currentBankInfo || {};

                                    // 银行卡类型和clearingCode不存在 则不能进行认证
                                    if (!clearingCode || !bankAccountMainType) {
                                        return BankCardErrorCodeTypes.NEED_COMPLETE_ACH_AUTH;
                                    }

                                    return true;
                                }

                                return __('请添加银行卡');
                            }
                        }}
                        onAdd={handleAdd}
                        onEdit={handleEdit}
                        onChange={handleChange}
                        customErrorConfigMap={{
                            [BankCardErrorCodeTypes.NEED_COMPLETE_ACH_AUTH]: {
                                msg: customErrorMsgMap[BankCardErrorCodeTypes.NEED_COMPLETE_ACH_AUTH],
                                btn: (
                                    <HashLink
                                        className="operable-btn"
                                        onClick={() => {
                                            currentBankInfo && handleEdit(currentBankInfo);
                                        }}>
                                        {__('去补充')} &gt;
                                    </HashLink>
                                )
                            }
                        }}
                        showStatusTag
                    />
                </div>
                <div className="space-box">
                    <p className="desc">
                        {__(
                            'Please upload a photo or screenshot of your bank statement or a blank check, which includes your full name and account number.'
                        )}
                    </p>
                    <div className="info">
                        <div className="account-name">
                            <label>{__('账户名称：')}</label>
                            <span className="value">
                                {holderName || security.webAccountInfo?.real_name_en || '- -'}
                            </span>
                        </div>
                        <div className="account-number">
                            <label>{__('账户号码：')}</label>
                            <span className="value">{bankAccount ? getMaskText(bankAccount) : '- -'}</span>
                        </div>
                    </div>
                </div>

                <div className="space-box">
                    <p className="desc">{__('最多支持上传6张图片或PDF文件，单张图片或文件不能超过5M。')}</p>
                    <UploaderList
                        maxCount={6}
                        isUploadFile
                        type="deposit"
                        name="files"
                        checker={(value: unknown) => {
                            if (!value?.[0]?.url) {
                                return __('请上传您的银行账户凭证');
                            }

                            return true;
                        }}
                        bucket="user-certificate"
                    />
                </div>
                <FormItem
                    className="auth-agreement"
                    name="agreement"
                    checker={value => !!value || __('Please click the box to agree')}>
                    <Checkbox>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: printf(
                                    __(
                                        'By clicking Submit, I acknowledge that I have fully read, understood, and agree to comply with the terms outlined in <a href="%s" target="_blank">Manual Bank Account Linkage Statement</a>.'
                                    ),
                                    `https://www.tradeup.com/statement/USA-Marsco-0017?lang=${language}`
                                )
                            }}
                        />
                    </Checkbox>
                </FormItem>
                <CardButtonWrap>
                    <Button
                        size="large"
                        block
                        onClick={() => {
                            navigate(-1);
                        }}>
                        {__('返回')}
                    </Button>
                    <Button size="large" block type="primary" htmlType="submit">
                        {__('提交认证')}
                    </Button>
                </CardButtonWrap>
                <Debug data={$params} />
            </Form>
        );
    };

    return (
        <div className="bankcards-auth-manual-root">
            <h3 className="main-title">{__('Upload your bank statement')}</h3>
            {render()}
        </div>
    );
};

export default withForm(Manual);
